import React, {useEffect, useState} from 'react';

import {getTransformDataTypes} from 'instruments/connections/ConnectionDAO';
import {QueryingConnectionSearchInput} from 'instruments/connections/ConnectionSearchInput';
import {
  InstrumentData,
  InstrumentDataFileType,
  InstrumentFileStatus,
} from 'instruments/instrument-data/InstrumentDataConstants';
import InstrumentDataTable from 'instruments/instrument-data/InstrumentDataTable';
import {FilterKey} from 'search/SearchConstants';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {LabelStyle} from 'ui-library/inputs/InputConstants';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Stepper, {StepperStep} from 'ui-library/stepper/Stepper';
import {capitalize, constantCaseToWords} from 'util/NameUtil';

import {ConnectionListingGraphQL} from '../../../../graphql-fragments/instruments/__generated/ConnectionListingGraphQL';
import {
  ColumnMappingStepperStepProps,
  getColumnMappingStepperStep,
} from '../column-mapping/ColumnMappingStepperStep';
import DataFrameNameField, {DataFrameNameFieldProps} from '../steps/DataFrameNameField';

interface InstrumentDataSelectorProps {
  connectionId: string;
  value: InstrumentData | null;
  onChange: (val: InstrumentData | null) => void;
}

function InstrumentDataSelector(props: InstrumentDataSelectorProps): JSX.Element {
  const [allInstrumentData, setAllInstrumentData] = useState<Array<InstrumentData>>([]);
  const {value} = props;

  const uploadedData = allInstrumentData.filter((data) => data.status !== InstrumentFileStatus.NOT_UPLOADED);
  const selectedIdx = value ? uploadedData.findIndex(({id}) => id === value.id) : -1;
  const selectedRowIndices = new Set(selectedIdx >= 0 ? [selectedIdx] : []);

  return (
    <InstrumentDataTable
      instrumentData={uploadedData}
      allowMultiple={false}
      setInstrumentData={setAllInstrumentData}
      selectedRowIndices={selectedRowIndices}
      onSelectRow={(rowIndex) => {
        const selectedFile = uploadedData[rowIndex];
        props.onChange(selectedFile.id === value?.id ? null : selectedFile);
      }}
      columns={['File name', 'Status', 'Modified at']}
      filterOptions={[]}
      initialFilterValues={{[FilterKey.CONNECTION]: [props.connectionId]}}
      fileTypeFilter={InstrumentDataFileType.ORIGINAL}
    />
  );
}

interface TransformDataTypeSelectorProps {
  integrationAdapterId: string;
  value: string | null;
  onChange: (transformDataType: string) => void;
}

function TransformDataTypeSelector({
  integrationAdapterId,
  value,
  onChange,
}: TransformDataTypeSelectorProps): JSX.Element {
  const [transformDataTypes, setTransformDataTypes] = useState<{[id: string]: string} | null>(null);

  useEffect(() => {
    getTransformDataTypes(integrationAdapterId).then((types) => {
      setTransformDataTypes(types);
    });
  }, [integrationAdapterId]);

  useEffect(() => {
    if (transformDataTypes != null) {
      const keys = Object.keys(transformDataTypes);
      if (keys.length === 1 && value == null) {
        onChange(keys[0]);
      }
    }
  }, [transformDataTypes, value, onChange]);

  if (transformDataTypes == null) {
    return <CenteredHorizontalThrobber />;
  }

  return (
    <>
      <FieldLabel label="Data type" labelStyle={LabelStyle.LABEL} isRequired={true} />
      <SelectDropdown
        displayName={value != null ? capitalize(constantCaseToWords(value)) : 'Select a data type'}
        menu={
          <Menu value={value} height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.FULL} scrollable={true}>
            {Object.entries(transformDataTypes).map(([dataType, _description]) => (
              <Option
                key={dataType}
                value={dataType}
                onSelect={() => {
                  onChange(dataType);
                }}
              >
                {capitalize(constantCaseToWords(dataType))}
              </Option>
            ))}
          </Menu>
        }
      />
    </>
  );
}

export interface ConnectionDataStepperFormProps {
  dataFrameNameFieldProps: DataFrameNameFieldProps;
  connectionId: string | null;
  integrationAdapterId: string | null;
  onChangeConnection: (connection: ConnectionListingGraphQL | null) => void;
  instrumentData: InstrumentData | null;
  onChangeInstrumentData: (instrumentData: InstrumentData | null) => void;
  transformDataType: string | null;
  onChangeTransformDataType: (transformDataType: string) => void;
  columnMappingProps?: ColumnMappingStepperStepProps;
}

export default function ConnectionDataStepperForm(props: ConnectionDataStepperFormProps): JSX.Element {
  return (
    <Stepper>
      <StepperStep title="Select connection">
        <QueryingConnectionSearchInput
          autoFocus={false}
          onChange={(connections) => {
            props.onChangeConnection(connections.length ? connections[0] : null);
          }}
          connectionIds={props.connectionId != null ? [props.connectionId] : []}
          shouldAllowMultiple={false}
        />
      </StepperStep>
      <StepperStep title="Choose file">
        {props.connectionId != null && (
          <InstrumentDataSelector
            connectionId={props.connectionId}
            value={props.instrumentData}
            onChange={props.onChangeInstrumentData}
          />
        )}
      </StepperStep>
      <StepperStep title="Choose data type">
        {props.integrationAdapterId != null && (
          <TransformDataTypeSelector
            integrationAdapterId={props.integrationAdapterId}
            value={props.transformDataType}
            onChange={props.onChangeTransformDataType}
          />
        )}
      </StepperStep>
      {props.columnMappingProps != null && getColumnMappingStepperStep(props.columnMappingProps)}
      <StepperStep title="Name table">
        <DataFrameNameField {...props.dataFrameNameFieldProps} />
      </StepperStep>
    </Stepper>
  );
}
